import { apiClient } from '@/utils/api-client'

const API_INTERNAL_BASE = '/profile/social'

/**
 * @typedef {Object} SocialIdentity
 * @property {string} provider - `github` or `google`
 * @property {string|null} email - The email the provider vouched for
 * @property {string|null} linked_at
 * @property {string|null} last_used_at
 * @property {boolean} can_unlink - Whether removing it leaves another way in
 *
 * @typedef {Object} SignInMethods
 * @property {boolean} has_password
 * @property {SocialIdentity[]} identities
 * @property {string[]} linkable_providers - Providers that can be connected now
 */

/**
 * What the profile knows before the API has answered: no password, nothing
 * connected, nothing to connect.
 *
 * @returns {SignInMethods}
 */
export function emptySignInMethods() {
    return { has_password: false, identities: [], linkable_providers: [] }
}

/**
 * How the signed-in user signs in: their password, and the GitHub and Google
 * identities they have connected.
 */
class ProfileSocialService {
    /**
     * @returns {Promise<SignInMethods>}
     */
    async list() {
        const response = await apiClient.get(API_INTERNAL_BASE)
        const data = response.data?.data ?? {}

        return {
            has_password: data.has_password === true,
            identities: Array.isArray(data.identities) ? data.identities : [],
            linkable_providers: Array.isArray(data.linkable_providers)
                ? data.linkable_providers
                : [],
        }
    }

    /**
     * Start connecting a provider. Resolves to the URL of the provider's
     * consent screen; the caller sends the browser there, and the provider
     * returns to the profile with the outcome.
     *
     * @param {string} provider - `github` or `google`
     * @returns {Promise<string>}
     */
    async connect(provider) {
        const response = await apiClient.post(
            API_INTERNAL_BASE + '/' + encodeURIComponent(provider)
        )

        return response.data?.data?.url
    }

    /**
     * Disconnect a provider. Rejects with the API's message when it is the
     * only way the user can sign in.
     *
     * @param {string} provider - `github` or `google`
     * @returns {Promise<void>}
     */
    async disconnect(provider) {
        await apiClient.delete(
            API_INTERNAL_BASE + '/' + encodeURIComponent(provider)
        )
    }
}

export default new ProfileSocialService()
